import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Money, Cardholder, Bank, Truck, Package, ClipboardText, Eye, ListChecks, Warning, CheckCircle, Download } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useDataCache } from '@/contexts/DataCache';
import { useSalesConfirmModal } from '@/components/common/SalesConfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import { ExpensesList } from '@/components/common/ExpensesList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DENOMS = ['1000', '500', '200', '100', '50', '20', '10', '5', '2', '1', '0.5'];

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

export const Cuts = () => {
  const { refreshProducts, sellers, shipments, refreshShipments } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { modal: salesConfirmModal, blockClose: blockCloseWithConfirm } = useSalesConfirmModal();
  const [tab, setTab] = useState('current');
  // Dialogs para editar método de pago y vendedor
  const [editPayDlg, setEditPayDlg] = useState({ open: false, method: 'efectivo', split: { efectivo: 0, tarjeta: 0, transferencia: 0 }, saving: false });
  const [editSellerDlg, setEditSellerDlg] = useState({ open: false, sellerId: '', saving: false });
  const [adminPwdDlg, setAdminPwdDlg] = useState({ open: false, password: '', purpose: null, saving: false });
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState(() => {
    try {
      const raw = sessionStorage.getItem('cuts_history_expanded_v1');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* */ }
    return new Set();
  });
  const toggleExpandedHistory = (id) => {
    setExpandedHistory(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { sessionStorage.setItem('cuts_history_expanded_v1', JSON.stringify([...next])); } catch { /* */ }
      return next;
    });
  };
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState(Object.fromEntries(DENOMS.map(d => [d, 0])));
  const [otherOverflow, setOtherOverflow] = useState('');
  const [otherNote, setOtherNote] = useState('');
  const [notes, setNotes] = useState('');
  const [closing, setClosing] = useState(false);

  // Detalle
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Detalle de venta
  const [saleDetailOpen, setSaleDetailOpen] = useState(false);
  const [saleDetail, setSaleDetail] = useState(null);
  const [fullEditDlg, setFullEditDlg] = useState({ open: false, sale: null, items: [], reason: '', saving: false, shipping_cost: '', shipment_id: null });

  // Gasto retroactivo en corte cerrado
  const [addExpDlg, setAddExpDlg] = useState({ open: false, cut: null, amount: '', category: 'otros', description: '', reason: '', saving: false });

  // ----- Verificación de transferencias -----
  const updateTransferVerification = async (cutId, saleId, patch) => {
    // Optimistic update en `history`
    setHistory(prev => prev.map(c => {
      if (c.id !== cutId) return c;
      const txns = (c.transfer_transactions || []).map(t =>
        t.sale_id === saleId ? { ...t, ...patch } : t
      );
      return { ...c, transfer_transactions: txns };
    }));
    try {
      await axios.patch(`${API}/sales/${saleId}/transfer-verification`, patch, { withCredentials: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar verificación');
      // Revertir reload
      loadHistory();
    }
  };

  // Cancelar venta
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const loadCurrent = async () => {
    try {
      const { data } = await axios.get(`${API}/cuts/current`, { withCredentials: true });
      setCurrent(data);
      try { sessionStorage.setItem('cuts_current_cache_v1', JSON.stringify(data)); } catch { /* */ }
    } catch (e) {
      toast.error('Error al cargar corte actual');
    }
  };

  // 🔒 Cajeros: historial y totales de cortes requieren autorización por Telegram (solo esa visita)
  const historyAccessRef = useRef(null);
  const [historyUnlocked, setHistoryUnlocked] = useState(false);
  const [cutAccess, setCutAccess] = useState({});
  const [accessWaiting, setAccessWaiting] = useState(null);

  const requestCutsAccess = async (kind, cutId = null) => {
    setAccessWaiting({ kind, cutId });
    try {
      const { data } = await axios.post(`${API}/cuts/access-request`, { kind, cut_id: cutId }, { withCredentials: true });
      if (data.status === 'approved') return data.id;
      toast.info('⏳ Solicitud enviada por Telegram, esperando aprobación…');
      for (let i = 0; i < 240; i++) {
        await new Promise(r => setTimeout(r, 2500));
        const { data: st } = await axios.get(`${API}/approvals/${data.id}`, { withCredentials: true });
        if (st.status === 'approved') { toast.success('✅ Autorizado'); return data.id; }
        if (['rejected', 'expired'].includes(st.status)) { toast.error('❌ Solicitud rechazada'); return undefined; }
      }
      toast.error('Tiempo de espera agotado');
      return undefined;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar la solicitud');
      return undefined;
    } finally {
      setAccessWaiting(null);
    }
  };

  const loadHistory = async () => {
    if (!isAdmin && !historyAccessRef.current) return;
    try {
      const q = historyAccessRef.current ? `?access_id=${historyAccessRef.current}` : '';
      const { data } = await axios.get(`${API}/cuts${q}`, { withCredentials: true });
      const closed = data.filter(c => c.status === 'closed');
      setHistory(closed);
      if (isAdmin) { try { sessionStorage.setItem('cuts_history_cache_v1', JSON.stringify(closed)); } catch { /* */ } }
    } catch (e) {
      if (e.response?.data?.detail === 'CUTS_ACCESS_REQUIRED') { historyAccessRef.current = null; setHistoryUnlocked(false); setHistory([]); return; }
      toast.error('Error al cargar historial');
    }
  };

  const unlockHistory = async () => {
    const id = await requestCutsAccess('history');
    if (id === undefined) return;
    historyAccessRef.current = id;
    setHistoryUnlocked(true);
    await loadHistory();
  };

  // Hidratación instantánea desde sessionStorage para evitar el "flash de cargando".
  // Los datos cacheados se reemplazan apenas terminan los fetch en background.
  useEffect(() => {
    try {
      const c = sessionStorage.getItem('cuts_current_cache_v1');
      if (c) setCurrent(JSON.parse(c));
      const h = isAdmin ? sessionStorage.getItem('cuts_history_cache_v1') : null;
      if (h) setHistory(JSON.parse(h));
    } catch { /* */ }
    // Cargar fresh en paralelo (no en serie como antes)
    Promise.all([loadCurrent(), loadHistory(), refreshShipments()]);
    const t = setInterval(loadCurrent, 15000); // refresh totales cada 15s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = current?.totals || { sales_count: 0, sales_total: 0, cash_total: 0, transfer_total: 0, card_total: 0, shipping_overflow: 0, exits_qty: 0, exits_count: 0 };

  // Conteo de ventas por método de pago (mixto cuenta como 1 venta de cada método
  // donde el split sea > 0).
  const paymentCounts = useMemo(() => {
    const acc = { efectivo: 0, tarjeta: 0, transferencia: 0, mixto: 0 };
    for (const s of (current?.sales || [])) {
      if (s.status === 'cancelled') continue;
      const pm = (s.payment_method || '').toLowerCase();
      if (pm === 'mixto') {
        acc.mixto += 1;
        const split = s.payment_split || {};
        if (Number(split.efectivo) > 0) acc.efectivo += 1;
        if (Number(split.tarjeta) > 0) acc.tarjeta += 1;
        if (Number(split.transferencia) > 0) acc.transferencia += 1;
      } else if (acc[pm] !== undefined) {
        acc[pm] += 1;
      }
    }
    return acc;
  }, [current?.sales]);

  const countedTotal = useMemo(() => {
    return DENOMS.reduce((sum, d) => sum + (parseFloat(d) * (parseInt(counts[d]) || 0)), 0);
  }, [counts]);

  const expectedCash = useMemo(() => {
    // NOTA: NO sumamos `shipping_overflow` porque ya está incluido en `cash_total`
    // (la venta creada en /shipments/pay tiene total = items + envío). Se muestra
    // como informativo, no se duplica.
    return (totals.cash_total || 0) + (totals.loans_total || 0) + (parseFloat(otherOverflow) || 0) - (totals.expenses_total || 0);
  }, [totals, otherOverflow]);

  const cashDiff = countedTotal - expectedCash;

  // Envíos sin asignar: bloquean el cierre del corte
  const unassignedShipments = useMemo(
    () => (shipments || []).filter(s => s.status === 'por_asignar'),
    [shipments]
  );

  // Modal de aprobación por envíos atrasados (>7 días)
  const [overdueDlg, setOverdueDlg] = useState({ open: false, shipments: [], sending: false });

  const requestOverdueApproval = async () => {
    setOverdueDlg(s => ({ ...s, sending: true }));
    try {
      await axios.post(`${API}/cuts/close-request-approval`, {
        counted_cash: counts,
        other_overflow: parseFloat(otherOverflow) || 0,
        other_overflow_note: otherNote || null,
        notes: notes || null,
      }, { withCredentials: true });
      toast.success('✅ Solicitud enviada al admin por Telegram. Al aprobar, el corte se cerrará.', { duration: 7000 });
      setOverdueDlg({ open: false, shipments: [], sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar solicitud');
      setOverdueDlg(s => ({ ...s, sending: false }));
    }
  };

  const handleCloseCut = async () => {
    // Refrescar shipments para asegurar que la lista está al día antes de cerrar
    try { await refreshShipments(); } catch { /* */ }
    // Pre-flight: confirmación de verificación (sólo si la sucursal lo tiene activo).
    // El modal queda bloqueando hasta que escriban la palabra; si no, no se cierra.
    const okConfirm = await blockCloseWithConfirm();
    if (!okConfirm) return;
    if (!window.confirm(`¿Cerrar el corte y abrir uno nuevo?\n\nContado: ${fmtMoney(countedTotal)}\nEsperado: ${fmtMoney(expectedCash)}\nDiferencia: ${fmtMoney(cashDiff)}`)) return;
    setClosing(true);
    try {
      const { data } = await axios.post(`${API}/cuts/close`, {
        counted_cash: counts,
        other_overflow: parseFloat(otherOverflow) || 0,
        other_overflow_note: otherNote || null,
        notes: notes || null,
      }, { withCredentials: true });
      // Aprobación pendiente por diferencia — no hay cierre inmediato
      if (data?.requires_approval && data?.status === 'pending') {
        toast.info(`Solicitud enviada al admin por Telegram. Diferencia: ${fmtMoney(data.diff)}. El corte se cerrará al aprobarse.`, { duration: 7000 });
        return;
      }
      toast.success(`Corte ${data.closed_cut.folio} cerrado. Nuevo: ${data.new_cut.folio}`, {
        action: { label: '📊 Ver reporte', onClick: () => window.location.href = '/cuts/report' },
        duration: 8000,
      });
      setCounts(Object.fromEntries(DENOMS.map(d => [d, 0])));
      setOtherOverflow(''); setOtherNote(''); setNotes('');
      await loadCurrent();
      await loadHistory();
      setTab('history');
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data?.detail;
      // 409 con code = requires_approval
      if (status === 409 && data && typeof data === 'object' && data.code === 'requires_approval') {
        // Sub-caso A: envíos atrasados (flujo anterior)
        if (data.reason === 'overdue_shipments' || Array.isArray(data.shipments)) {
          setOverdueDlg({ open: true, shipments: data.shipments || [], sending: false });
          return;
        }
        // Sub-caso B: diferencia de caja — pedir motivo y re-enviar
        if (data.reason === 'cash_diff') {
          const diff = data.diff || 0;
          const reason = window.prompt(
            `El conteo tiene una diferencia de ${diff >= 0 ? '+' : ''}${diff.toFixed(2)}.\n` +
            `Esperado: ${fmtMoney(data.expected_cash)}\n` +
            `Contado:  ${fmtMoney(data.counted_total)}\n\n` +
            `Escribe el motivo del ${diff > 0 ? 'sobrante' : 'faltante'}. Se enviará al admin por Telegram para aprobación.`
          );
          if (!reason || !reason.trim()) {
            toast.error('Se requiere un motivo para cerrar con diferencia');
            return;
          }
          try {
            const { data: apr } = await axios.post(`${API}/cuts/close`, {
              counted_cash: counts,
              other_overflow: parseFloat(otherOverflow) || 0,
              other_overflow_note: otherNote || null,
              notes: notes || null,
              diff_reason: reason.trim(),
            }, { withCredentials: true });
            toast.info(`Solicitud enviada al admin. Diferencia: ${fmtMoney(apr.diff || diff)}. El corte se cerrará al aprobarse.`, { duration: 7000 });
          } catch (err2) {
            const d2 = err2?.response?.data?.detail;
            toast.error(typeof d2 === 'string' ? d2 : (d2?.message || 'Error al solicitar aprobación'));
          }
          return;
        }
      }
      toast.error(typeof data === 'string' ? data : (data?.message || 'Error al cerrar corte'));
    } finally {
      setClosing(false);
    }
  };

  const cutDetailUrl = (cutId, accessMap = cutAccess) => `${API}/cuts/${cutId}${accessMap[cutId] ? `?access_id=${accessMap[cutId]}` : ''}`;

  const openDetail = async (cutId, accessMap = cutAccess) => {
    const histCut = history.find(h => h.id === cutId);
    if (histCut?.totals_hidden && !accessMap[cutId]) {
      const id = await requestCutsAccess('cut_total', cutId);
      if (id === undefined) return;
      const next = { ...accessMap, [cutId]: id };
      setCutAccess(next);
      return openDetail(cutId, next);
    }
    setDetail(null);
    setDetailOpen(true);
    try {
      const { data } = await axios.get(cutDetailUrl(cutId, accessMap), { withCredentials: true });
      setDetail(data);
    } catch (e) {
      const denied = e.response?.data?.detail === 'CUTS_ACCESS_REQUIRED';
      toast.error(denied ? 'La autorización expiró, vuelve a solicitarla' : 'Error al cargar detalle');
      if (denied) setCutAccess(m => { const n = { ...m }; delete n[cutId]; return n; });
      setDetailOpen(false);
    }
  };

  const openSaleDetail = async (saleOrId) => {
    // Soporte legacy: si recibimos un id string, busca la venta en current/history.
    let sale = null;
    if (typeof saleOrId === 'object' && saleOrId) {
      sale = saleOrId;
    } else {
      const id = saleOrId;
      sale = (current?.sales || []).find(s => s.id === id) || null;
      if (!sale && detail) {
        sale = (detail.sales || []).find(s => s.id === id) || null;
      }
    }
    if (sale) {
      // Apertura instantánea con datos ya disponibles del corte.
      setSaleDetail({ ...sale, commissions: [], shipments: [] });
      setSaleDetailOpen(true);
      // Enriquecer en background con comisiones + envíos vinculados.
      try {
        const { data } = await axios.get(`${API}/sales/${sale.id}`, { withCredentials: true });
        setSaleDetail(data);
      } catch {/* mantiene los datos inline */}
      return;
    }
    // Fallback: si no tenemos la venta en memoria, hacemos la carga full.
    setSaleDetail(null);
    setSaleDetailOpen(true);
    try {
      const id = typeof saleOrId === 'string' ? saleOrId : saleOrId?.id;
      const { data } = await axios.get(`${API}/sales/${id}`, { withCredentials: true });
      setSaleDetail(data);
    } catch (e) {
      toast.error('Error al cargar venta');
      setSaleDetailOpen(false);
    }
  };


  const openFullEdit = (sale) => {
    const items = (sale.items || []).map((it) => ({
      product_id: it.product_id || null,
      package_id: it.package_id || null,
      is_package: !!it.package_id,
      product_name: it.product_name || it.package_name || it.name || '—',
      variant_id: it.variant_id || null,
      variant_name: it.variant_name || null,
      quantity: it.quantity || 1,
      price: it.unit_price ?? it.price ?? 0,
      commission: it.commission || 0,
    }));
    // Precargar el costo del PRIMER envío ligado (si existe)
    const firstShipment = (sale.shipments && sale.shipments[0]) || null;
    setFullEditDlg({
      open: true, sale, items, reason: '', saving: false,
      shipping_cost: firstShipment ? String(firstShipment.shipping_cost ?? '') : '',
      shipment_id: firstShipment ? firstShipment.id : null,
    });
  };

  const updateFullEditItem = (idx, field, value) => {
    setFullEditDlg(s => {
      const items = [...s.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...s, items };
    });
  };

  const removeFullEditItem = (idx) => {
    setFullEditDlg(s => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));
  };

  const submitFullEdit = async () => {
    if (!fullEditDlg.sale) return;
    // Tolerante: aceptamos ítems huérfanos (sin product_id ni package_id) siempre
    // que tengan nombre y cantidad > 0 — el backend los preserva tal cual.
    const items = fullEditDlg.items
      .filter(it => Number(it.quantity) > 0 && (it.product_id || it.package_id || (it.product_name && it.product_name !== '—')))
      .map(it => ({
        product_id: it.product_id || null,
        package_id: it.package_id || null,
        // Nombre por si el backend necesita preservarlo (ítem huérfano)
        product_name: it.product_name || null,
        variant_id: it.variant_id || undefined,
        quantity: Number(it.quantity),
        price: Number(it.price) || 0,
        commission: Number(it.commission) || 0,
      }));
    if (items.length === 0) { toast.error('Debe quedar al menos un item'); return; }
    // Costo de envío opcional: sólo se envía si hay envío ligado y el usuario cambió algo
    const shippingCostNum = fullEditDlg.shipping_cost === '' ? null : Number(fullEditDlg.shipping_cost);
    if (shippingCostNum !== null && (isNaN(shippingCostNum) || shippingCostNum < 0)) {
      toast.error('Costo de envío inválido'); return;
    }
    setFullEditDlg(s => ({ ...s, saving: true }));
    try {
      const { data } = await axios.post(
        `${API}/sales/${fullEditDlg.sale.id}/full-edit`,
        {
          items,
          reason: fullEditDlg.reason,
          shipping_cost: shippingCostNum,
          shipment_id: fullEditDlg.shipment_id,
        },
        { withCredentials: true }
      );
      if (data.status === 'pending') {
        toast.success('✅ Solicitud enviada al admin para aprobación');
      } else {
        toast.success(`Venta actualizada · nuevo total $${(data.new_total || 0).toFixed(2)}`);
      }
      setFullEditDlg({ open: false, sale: null, items: [], reason: '', saving: false });
      setSaleDetailOpen(false);
      await loadCurrent();
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al editar venta');
      setFullEditDlg(s => ({ ...s, saving: false }));
    }
  };

  // ----- Gasto retroactivo en corte cerrado -----
  const openAddExpense = (cut) => {
    setAddExpDlg({ open: true, cut, amount: '', category: 'otros', description: '', reason: '', saving: false });
  };

  const submitAddExpense = async () => {
    const amt = Number(addExpDlg.amount);
    if (!amt || amt <= 0) { toast.error('Indica un monto válido'); return; }
    if (addExpDlg.category === 'otros' && !addExpDlg.description.trim()) {
      toast.error('Describe el gasto cuando es "otros"');
      return;
    }
    if (!isAdmin && !addExpDlg.reason.trim()) {
      toast.error('Indica el motivo para solicitar aprobación');
      return;
    }
    setAddExpDlg(s => ({ ...s, saving: true }));
    try {
      const { data } = await axios.post(
        `${API}/cuts/${addExpDlg.cut.id}/add-expense`,
        {
          amount: amt,
          category: addExpDlg.category,
          description: addExpDlg.description.trim(),
          reason: addExpDlg.reason.trim(),
        },
        { withCredentials: true }
      );
      if (data?.status === 'pending') {
        toast.success('✅ Solicitud enviada al admin para aprobación');
      } else {
        toast.success('Gasto agregado al corte');
        // Refrescar detalle del corte abierto
        if (detail?.id === addExpDlg.cut.id) {
          try {
            const { data: fresh } = await axios.get(cutDetailUrl(addExpDlg.cut.id), { withCredentials: true });
            setDetail(fresh);
          } catch { /* */ }
        }
        await loadHistory();
      }
      setAddExpDlg({ open: false, cut: null, amount: '', category: 'otros', description: '', reason: '', saving: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al agregar gasto');
      setAddExpDlg(s => ({ ...s, saving: false }));
    }
  };


  const openCancel = (sale) => {
    setCancelTarget({ ...sale, _kind: 'sale' });
    setCancelReason('');
    setCancelOpen(true);
  };

  const openCancelShipment = (sh) => {
    setCancelTarget({ ...sh, _kind: 'shipment' });
    setCancelReason('');
    setCancelOpen(true);
  };

  const cancelExpense = async (exp) => {
    const label = exp.description || exp.category || 'el gasto';
    if (!window.confirm(`¿Cancelar ${label} por ${fmtMoney(exp.amount || 0)}?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/expenses/${exp.id}`, { withCredentials: true });
      toast.success('Gasto cancelado');
      await loadCurrent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Escribe el motivo'); return; }
    setCancelling(true);
    try {
      const isShipment = cancelTarget?._kind === 'shipment';
      const url = isShipment
        ? `${API}/shipments/${cancelTarget.id}/cancel-paid`
        : `${API}/sales/${cancelTarget.id}/cancel`;
      const { data } = await axios.post(url, { reason: cancelReason }, { withCredentials: true });
      if (data.status === 'pending') {
        toast.success('Solicitud enviada al admin para aprobación');
      } else {
        toast.success(isShipment ? 'Envío cancelado' : 'Venta cancelada · stock restaurado');
        // Refrescar cache de productos para que el stock restaurado se vea en POS/Inventario
        refreshProducts();
      }
      setCancelOpen(false);
      setSaleDetailOpen(false);
      await loadCurrent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al solicitar cancelación');
    } finally {
      setCancelling(false);
    }
  };

  // Abre el editor de método de pago precargando el split actual (si lo hay)
  const openEditPayment = () => {
    if (!saleDetail) return;
    const split = saleDetail.payment_split || {};
    const total = Number(saleDetail.total || 0);
    setEditPayDlg({
      open: true,
      method: saleDetail.payment_method || 'efectivo',
      split: {
        efectivo: Number(split.efectivo) || (saleDetail.payment_method === 'efectivo' ? total : 0),
        tarjeta: Number(split.tarjeta) || (saleDetail.payment_method === 'tarjeta' ? total : 0),
        transferencia: Number(split.transferencia) || (saleDetail.payment_method === 'transferencia' ? total : 0),
      },
      saving: false,
    });
  };

  const submitEditPayment = async () => {
    if (!saleDetail) return;
    setEditPayDlg(s => ({ ...s, saving: true }));
    try {
      const body = { payment_method: editPayDlg.method };
      if (editPayDlg.method === 'mixto') {
        body.payment_split = editPayDlg.split;
      }
      const { data } = await axios.patch(`${API}/sales/${saleDetail.id}/payment`, body, { withCredentials: true });
      if (data.status === 'pending') {
        toast.info('⏳ Solicitud enviada al admin para aprobación por Telegram');
      } else {
        toast.success('Método de pago actualizado');
        const { data: fresh } = await axios.get(`${API}/sales/${saleDetail.id}`, { withCredentials: true });
        setSaleDetail(fresh);
        await loadCurrent();
      }
      setEditPayDlg(s => ({ ...s, open: false, saving: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al actualizar pago');
      setEditPayDlg(s => ({ ...s, saving: false }));
    }
  };

  const submitEditSeller = async () => {
    if (!saleDetail || !editSellerDlg.sellerId) {
      toast.error('Selecciona un vendedor');
      return;
    }
    if (isAdmin) {
      // Admin: pedir contraseña antes de aplicar
      setAdminPwdDlg({ open: true, password: '', purpose: 'seller', saving: false });
      return;
    }
    // Clerk: enviar directamente solicitud de aprobación
    setEditSellerDlg(s => ({ ...s, saving: true }));
    try {
      const { data } = await axios.patch(
        `${API}/sales/${saleDetail.id}/seller`,
        { seller_id: editSellerDlg.sellerId },
        { withCredentials: true }
      );
      if (data.status === 'pending') {
        toast.info('⏳ Solicitud enviada al admin para aprobación por Telegram');
      } else {
        toast.success('Vendedor actualizado');
        const { data: fresh } = await axios.get(`${API}/sales/${saleDetail.id}`, { withCredentials: true });
        setSaleDetail(fresh);
        await loadCurrent();
      }
      setEditSellerDlg(s => ({ ...s, open: false, saving: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al solicitar cambio');
      setEditSellerDlg(s => ({ ...s, saving: false }));
    }
  };

  const confirmAdminPwd = async () => {
    setAdminPwdDlg(s => ({ ...s, saving: true }));
    try {
      // Verificar contraseña de admin
      await axios.post(`${API}/auth/verify-admin-password`, { password: adminPwdDlg.password }, { withCredentials: true });
      // Si pasó, aplicar el cambio
      if (adminPwdDlg.purpose === 'seller') {
        await axios.patch(
          `${API}/sales/${saleDetail.id}/seller`,
          { seller_id: editSellerDlg.sellerId },
          { withCredentials: true }
        );
        toast.success('Vendedor actualizado · comisiones reasignadas');
        const { data } = await axios.get(`${API}/sales/${saleDetail.id}`, { withCredentials: true });
        setSaleDetail(data);
        setEditSellerDlg(s => ({ ...s, open: false, saving: false }));
        await loadCurrent();
      }
      setAdminPwdDlg({ open: false, password: '', purpose: null, saving: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Contraseña incorrecta o error al aplicar el cambio');
      setAdminPwdDlg(s => ({ ...s, saving: false }));
    }
  };

  // Atajo Ctrl+Shift+V: cambiar vendedor de la venta abierta (mismo que el POS)
  useEffect(() => {
    const onKey = (e) => {
      if (!saleDetailOpen || !saleDetail || !isAdmin) return;
      if (e.ctrlKey && e.shiftKey && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        setEditSellerDlg({ open: true, sellerId: saleDetail.seller_id || '', saving: false });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saleDetailOpen, saleDetail, isAdmin]);

  return (
    <MainLayout>
      {salesConfirmModal}
      <div className="container max-w-7xl py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Corte de Caja</h1>
            <p className="text-muted-foreground">Conciliación de efectivo, ventas y movimientos.</p>
          </div>
          {isAdmin && (
          <Link to="/cuts/report">
            <Button variant="outline" data-testid="link-cuts-report">
              <Download size={18} weight="bold" className="mr-2" />
              Reporte / Excel
            </Button>
          </Link>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="current" data-testid="tab-current-cut">📋 Corte actual</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">🗂️ Historial ({history.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="current" className="space-y-4">
            {!current ? (
              <p>Cargando…</p>
            ) : (
              <>
                <Card>
                  <CardHeader className="flex-row items-center justify-between">
                    <div>
                      <CardTitle>{current.folio}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Abierto {fmtDateTime(current.started_at)} · {totals.sales_count} ventas
                      </p>
                    </div>
                    <Badge className="bg-emerald-600">Abierto</Badge>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryCard
                        icon={<Money size={20} weight="bold" />}
                        label="Efectivo"
                        value={fmtMoney(totals.cash_total)}
                        color="emerald"
                        subtle={`${paymentCounts.efectivo} ${paymentCounts.efectivo === 1 ? 'venta' : 'ventas'}`}
                      />
                      <SummaryCard
                        icon={<Bank size={20} weight="bold" />}
                        label="Transferencia"
                        value={fmtMoney(totals.transfer_total)}
                        color="sky"
                        subtle={`${paymentCounts.transferencia} ${paymentCounts.transferencia === 1 ? 'venta' : 'ventas'}`}
                      />
                      <SummaryCard
                        icon={<Cardholder size={20} weight="bold" />}
                        label="Tarjeta"
                        value={fmtMoney(totals.card_total)}
                        color="violet"
                        subtle={`${paymentCounts.tarjeta} ${paymentCounts.tarjeta === 1 ? 'venta' : 'ventas'}`}
                      />
                      <SummaryCard
                        icon={<Truck size={20} weight="bold" />}
                        label="Sobrante envío"
                        value={fmtMoney(totals.shipping_overflow)}
                        color="amber"
                        subtle={(() => {
                          const by = totals.shipping_overflow_by_method || {};
                          const parts = [];
                          if (by.transferencia > 0) parts.push(`${fmtMoney(by.transferencia)} transferencia`);
                          if (by.efectivo > 0) parts.push(`${fmtMoney(by.efectivo)} efectivo`);
                          if (by.tarjeta > 0) parts.push(`${fmtMoney(by.tarjeta)} tarjeta`);
                          return parts.length > 0 ? parts.join(' · ') : null;
                        })()}
                      />
                    </div>
                    {paymentCounts.mixto > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        🔀 <b>{paymentCounts.mixto}</b> {paymentCounts.mixto === 1 ? 'venta mixta' : 'ventas mixtas'} (contadas en las pills de arriba según las partes usadas)
                      </div>
                    )}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                      <SummaryCard
                        icon={<Package size={20} weight="bold" />}
                        label="Faltantes (gastos)"
                        value={fmtMoney(totals.expenses_total || 0)}
                        color="rose"
                        subtle={`${totals.expenses_count || 0} gastos · ${totals.exits_qty || 0} pzas en salidas`}
                      />
                      <SummaryCard icon={<ListChecks size={20} weight="bold" />} label="Total ventas" value={fmtMoney(totals.sales_total)} color="indigo" />
                      <SummaryCard icon={<Money size={20} weight="bold" />} label="Sobrante otros" value={fmtMoney(parseFloat(otherOverflow) || 0)} color="teal" />
                    </div>
                    {totals.expenses_by_cat && Object.keys(totals.expenses_by_cat).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        {Object.entries(totals.expenses_by_cat).map(([k, v]) => (
                          <div key={k} className="bg-rose-50 border border-rose-200 rounded px-2 py-1">
                            <b>{k.replace('_', ' ')}:</b> {fmtMoney(v)}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="grid lg:grid-cols-3 gap-4">
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>💵 Conteo de efectivo</CardTitle>
                      <p className="text-sm text-muted-foreground">Cuenta los billetes y monedas en caja.</p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                        {DENOMS.map(d => (
                          <div key={d} className="border rounded p-2 bg-slate-50">
                            <Label className="text-xs text-muted-foreground">${d}</Label>
                            <Input
                              type="number"
                              min="0"
                              value={counts[d]}
                              onChange={(e) => setCounts(prev => ({ ...prev, [d]: parseInt(e.target.value) || 0 }))}
                              data-testid={`denom-${d}`}
                              className="font-bold text-center"
                            />
                            <p className="text-xs text-right mt-1 font-medium text-slate-700">
                              {fmtMoney(parseFloat(d) * (parseInt(counts[d]) || 0))}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>🧾 Resumen del corte</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Row label="💵 Efectivo (ventas + envíos)" value={fmtMoney(totals.cash_total)} />
                      <Row label="🏦 Transferencia (ventas + envíos)" value={fmtMoney(totals.transfer_total)} color="sky" />
                      <Row label="💳 Tarjeta (ventas + envíos)" value={fmtMoney(totals.card_total)} color="violet" />
                      <Row
                        label="🚚 Sobrante envío"
                        value={fmtMoney(totals.shipping_overflow)}
                        subtle={(() => {
                          const by = totals.shipping_overflow_by_method || {};
                          const parts = [];
                          if (by.transferencia > 0) parts.push(`${fmtMoney(by.transferencia)} transferencia`);
                          if (by.efectivo > 0) parts.push(`${fmtMoney(by.efectivo)} efectivo`);
                          if (by.tarjeta > 0) parts.push(`${fmtMoney(by.tarjeta)} tarjeta`);
                          return parts.length > 0 ? `(${parts.join(' + ')})` : 'ya incluido arriba';
                        })()}
                      />
                      {(totals.loans_total || 0) > 0 && (
                        <Row label={`💸 Préstamos liquidados (sobrante) · ${(totals.loans || []).map(l => l.borrower_name).join(', ')} — mete el efectivo del sobre a la caja`} value={`+${fmtMoney(totals.loans_total)}`} color="emerald" testId="cut-loans-total" />
                      )}
                      <Row label="🧾 Gastos (faltantes)" value={`-${fmtMoney(totals.expenses_total || 0)}`} color="rose" />
                      <div>
                        <Label className="text-xs">Sobrante otros (manual)</Label>
                        <Input
                          type="number" step="0.01"
                          value={otherOverflow}
                          onChange={(e) => setOtherOverflow(e.target.value)}
                          placeholder="0.00"
                          data-testid="other-overflow"
                        />
                        <Input
                          className="mt-1 text-xs"
                          placeholder="Concepto (opcional)"
                          value={otherNote}
                          onChange={(e) => setOtherNote(e.target.value)}
                        />
                      </div>
                      <div className="border-t pt-3 space-y-2">
                        <Row label="Esperado en caja" value={fmtMoney(expectedCash)} bold />
                        <Row label="Contado" value={fmtMoney(countedTotal)} bold testId="counted-total" />
                        <Row
                          label="Diferencia"
                          value={fmtMoney(cashDiff)}
                          color={cashDiff === 0 ? 'emerald' : (cashDiff > 0 ? 'sky' : 'rose')}
                          bold
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Notas</Label>
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                      </div>
                      <Button
                        onClick={handleCloseCut}
                        disabled={closing || unassignedShipments.length > 0}
                        className="w-full bg-rose-600 hover:bg-rose-700"
                        data-testid="close-cut-btn"
                        title={unassignedShipments.length > 0 ? 'Debes asignar o cancelar todos los envíos pendientes antes de cerrar' : undefined}
                      >
                        {closing ? 'Cerrando…' : '🔒 Cerrar corte y abrir nuevo'}
                      </Button>
                      {unassignedShipments.length > 0 && (
                        <div className="bg-rose-50 border border-rose-300 rounded p-2 text-xs text-rose-800" data-testid="unassigned-shipments-warning">
                          ⚠️ No puedes cerrar el corte. Hay <b>{unassignedShipments.length}</b> envío{unassignedShipments.length === 1 ? '' : 's'} sin asignar entregador:
                          <ul className="mt-1 list-disc list-inside">
                            {unassignedShipments.slice(0, 5).map(sh => (
                              <li key={sh.id}>
                                <b>{sh.folio}</b>{sh.client_name ? ` · ${sh.client_name}` : ''}{sh.items_total ? ` · ${fmtMoney(sh.items_total)}` : ''}
                              </li>
                            ))}
                            {unassignedShipments.length > 5 && (
                              <li>+{unassignedShipments.length - 5} más…</li>
                            )}
                          </ul>
                          <Link to="/shipments" className="inline-block mt-1 underline font-semibold hover:text-rose-900">
                            Ir a Envíos →
                          </Link>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>📋 Movimientos del corte ({(current.sales || []).length} ventas · {(current.paid_shipments || []).length} envíos cobrados)</CardTitle>
                    <p className="text-sm text-muted-foreground">Haz click en una venta para ver el detalle o cancelarla.</p>
                  </CardHeader>
                  <CardContent>
                    {(current.sales || []).length === 0 && (current.paid_shipments || []).length === 0 ? (
                      <p className="text-muted-foreground text-sm">Aún no hay movimientos en este corte.</p>
                    ) : (
                      <div className="space-y-1">
                        {(current.sales || []).map(s => {
                          const pmStyles = {
                            efectivo: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                            tarjeta: 'bg-sky-100 text-sky-800 border-sky-300',
                            transferencia: 'bg-amber-100 text-amber-800 border-amber-300',
                            mixto: 'bg-violet-100 text-violet-800 border-violet-300',
                          };
                          const pmIcons = { efectivo: '💵', tarjeta: '💳', transferencia: '🏦', mixto: '🔀' };
                          const pm = (s.payment_method || '').toLowerCase();
                          return (
                          <div
                            key={s.id}
                            className={`flex items-center gap-3 border rounded p-2 hover:bg-violet-50 cursor-pointer ${s.status === 'cancelled' ? 'opacity-50 line-through' : ''}`}
                            onClick={() => openSaleDetail(s)}
                            data-testid={`current-sale-${s.id}`}
                          >
                            <Badge className="bg-violet-600">VENTA</Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${pmStyles[pm] || 'bg-slate-100 text-slate-700 border-slate-300'}`}
                              data-testid={`sale-pm-${s.id}`}
                            >
                              {pmIcons[pm] || '•'} {s.payment_method || '—'}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm">
                                {s.seller_name || '—'}
                                {s.has_shipment && <Badge variant="outline" className="ml-2 text-xs">📦 con envío</Badge>}
                                {s.status === 'cancelled' && <Badge variant="destructive" className="ml-2 text-xs">Cancelada</Badge>}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {fmtDateTime(s.created_at)} · {(s.items || []).length} ítems
                                {pm === 'mixto' && s.payment_split && (
                                  <span className="ml-1">
                                    {' · '}
                                    {Object.entries(s.payment_split)
                                      .filter(([, v]) => Number(v) > 0)
                                      .map(([k, v]) => `${k} ${fmtMoney(v)}`)
                                      .join(' + ')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-base">{fmtMoney(s.total)}</div>
                            </div>
                          </div>
                          );
                        })}
                        {(current.paid_shipments || []).map(sh => (
                          <div
                            key={sh.id}
                            className="flex items-center gap-3 border rounded p-2 bg-amber-50 border-amber-200"
                          >
                            <Badge className="bg-amber-600">ENVÍO COBRADO</Badge>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm">{sh.folio} · {sh.client_name || '—'}</div>
                              <div className="text-xs text-muted-foreground">
                                Pagado {fmtDateTime(sh.paid_at)} · costo envío {fmtMoney(sh.shipping_cost)}
                                {sh.deliverer_keeps_shipping && <span className="ml-1">(auto propio)</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-base">{fmtMoney(sh.items_total)}</div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openCancelShipment(sh)}
                                className="text-rose-700 hover:bg-rose-50 mt-1 h-7 px-2 text-xs"
                                data-testid={`cancel-shipment-${sh.id}`}
                              >
                                Cancelar envío
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Gastos del corte (con cancelar) */}
                {(current.expenses || []).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>🧾 Gastos del corte ({(current.expenses || []).length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ExpensesList expenses={current.expenses || []} onCancel={cancelExpense} />
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3">
            {!isAdmin && !historyUnlocked ? (
              <Card data-testid="history-locked"><CardContent className="py-10 text-center space-y-3">
                <div className="text-4xl">🔒</div>
                <p className="text-sm text-muted-foreground">Para ver el historial de cortes se necesita autorización por Telegram.</p>
                <Button onClick={unlockHistory} disabled={!!accessWaiting} data-testid="request-history-access">
                  {accessWaiting?.kind === 'history' ? '⏳ Esperando aprobación…' : 'Solicitar autorización'}
                </Button>
              </CardContent></Card>
            ) : history.length === 0 ? (
              <p className="text-muted-foreground">Aún no hay cortes cerrados.</p>
            ) : (
              <div className="space-y-2">
                {history.map(c => {
                  const isExpanded = expandedHistory.has(c.id);
                  const txns = c.transfer_transactions || [];
                  const errorCount = txns.filter(t => t.has_error).length;
                  const verifiedCount = txns.filter(t => t.verified).length;
                  return (
                  <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`cut-row-${c.id}`}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between gap-4 cursor-pointer" onClick={() => openDetail(c.id)}>
                        <div className="min-w-0">
                          <div className="font-semibold flex items-center gap-2">
                            {c.folio}
                            <Badge variant="outline">{c.totals?.sales_count || c.sales_count || 0} ventas</Badge>
                            {c.transfer_total > 0 && (
                              <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-300">
                                🏦 {fmtMoney(c.transfer_total)}
                              </Badge>
                            )}
                            {errorCount > 0 && (
                              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300" data-testid={`error-badge-${c.id}`}>
                                🚩 {errorCount} con error
                              </Badge>
                            )}
                            {txns.length > 0 && verifiedCount === txns.length && (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                                ✓ Verificadas
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {fmtDateTime(c.started_at)} → {fmtDateTime(c.closed_at)} · cierre: {c.closed_by_name}
                          </p>
                        </div>
                        {c.totals_hidden ? (
                          <div className="text-right text-sm hidden sm:block text-muted-foreground" data-testid={`cut-total-hidden-${c.id}`}>
                            <div><b>$ •••••</b> en ventas</div>
                            <div className="text-xs">{accessWaiting?.cutId === c.id ? '⏳ Esperando aprobación…' : 'Toca 👁 para ver (requiere aprobación)'}</div>
                          </div>
                        ) : (
                        <div className="text-right text-sm hidden sm:block">
                          <div><b>{fmtMoney(c.totals?.sales_total || c.sales_total || 0)}</b> en ventas</div>
                          <div className={`text-xs ${(c.cash_diff || 0) === 0 ? 'text-emerald-600' : (c.cash_diff > 0 ? 'text-sky-600' : 'text-rose-600')}`}>
                            Dif: {fmtMoney(c.cash_diff || 0)}
                          </div>
                        </div>
                        )}
                        <Button variant="ghost" size="icon" disabled={!!accessWaiting} data-testid={`cut-eye-${c.id}`} onClick={(e) => { e.stopPropagation(); openDetail(c.id); }}><Eye size={18} /></Button>
                      </div>
                      {(c.notes || c.diff_reason || c.other_overflow_note) && (
                        <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5 space-y-0.5" data-testid={`cut-notes-${c.id}`}>
                          {c.notes && <div>📝 <b>Notas:</b> <span className="whitespace-pre-wrap">{c.notes}</span></div>}
                          {c.diff_reason && <div>⚖️ <b>Motivo diferencia:</b> {c.diff_reason}</div>}
                          {c.other_overflow_note && <div>💵 <b>Sobrante otros (manual):</b> {c.other_overflow_note}{!c.totals_hidden && c.other_overflow ? ` · ${fmtMoney(c.other_overflow)}` : ''}</div>}
                        </div>
                      )}
                      {txns.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpandedHistory(c.id); }}
                            className="text-xs text-sky-700 hover:text-sky-900 font-medium"
                            data-testid={`expand-transfers-${c.id}`}
                          >
                            {isExpanded ? '▼' : '▶'} {txns.length} transferencia{txns.length !== 1 ? 's' : ''}{!c.totals_hidden && <> · Total {fmtMoney(c.transfer_total)}</>}
                            {verifiedCount > 0 && <span className="text-emerald-600 ml-1">· ✓ {verifiedCount}</span>}
                            {errorCount > 0 && <span className="text-rose-600 ml-1">· 🚩 {errorCount}</span>}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 max-h-80 overflow-y-auto border rounded text-xs" onClick={(e) => e.stopPropagation()}>
                              <table className="w-full">
                                <thead className="bg-slate-50 sticky top-0">
                                  <tr>
                                    <th className="text-left px-2 py-1">Fecha</th>
                                    <th className="text-left px-2 py-1">Cliente</th>
                                    <th className="text-left px-2 py-1">Vendedor</th>
                                    <th className="text-right px-2 py-1">Transferencia</th>
                                    <th className="text-center px-2 py-1">Tipo</th>
                                    {isAdmin && <th className="text-center px-2 py-1" title="Verificada">✓</th>}
                                    {isAdmin && <th className="text-center px-2 py-1" title="Error">🚩</th>}
                                    {isAdmin && <th className="text-left px-2 py-1">Nota</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {txns.map((t) => {
                                    const rowClass = t.has_error
                                      ? 'border-t bg-rose-50 hover:bg-rose-100'
                                      : (t.verified ? 'border-t bg-emerald-50/40 hover:bg-emerald-50' : 'border-t hover:bg-sky-50');
                                    return (
                                    <tr
                                      key={t.sale_id}
                                      className={rowClass}
                                      data-testid={`transfer-txn-${t.sale_id}`}
                                    >
                                      <td
                                        className="px-2 py-1 cursor-pointer"
                                        onClick={() => openSaleDetail(t.sale_id)}
                                      >{fmtDateTime(t.created_at)}</td>
                                      <td
                                        className="px-2 py-1 cursor-pointer"
                                        onClick={() => openSaleDetail(t.sale_id)}
                                      >{t.client_name || '—'}</td>
                                      <td
                                        className="px-2 py-1 cursor-pointer"
                                        onClick={() => openSaleDetail(t.sale_id)}
                                      >{t.seller_name || '—'}</td>
                                      <td
                                        className="px-2 py-1 text-right font-semibold text-sky-700 cursor-pointer"
                                        onClick={() => openSaleDetail(t.sale_id)}
                                      >{fmtMoney(t.amount)}</td>
                                      <td className="px-2 py-1 text-center">
                                        {t.is_mixed ? <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 rounded">mixto</span> : <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 rounded">único</span>}
                                      </td>
                                      {isAdmin && (
                                        <td className="px-2 py-1 text-center">
                                          <input
                                            type="checkbox"
                                            checked={!!t.verified}
                                            onChange={(e) => updateTransferVerification(c.id, t.sale_id, { verified: e.target.checked })}
                                            className="cursor-pointer h-4 w-4 accent-emerald-600"
                                            title={t.verified ? 'Verificada — click para quitar' : 'Marcar como verificada'}
                                            data-testid={`verify-check-${t.sale_id}`}
                                          />
                                        </td>
                                      )}
                                      {isAdmin && (
                                        <td className="px-2 py-1 text-center">
                                          <button
                                            type="button"
                                            onClick={() => updateTransferVerification(c.id, t.sale_id, { has_error: !t.has_error })}
                                            className={`px-2 py-0.5 rounded text-[11px] font-semibold transition ${t.has_error ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-700'}`}
                                            title={t.has_error ? 'Hay un error — click para quitar' : 'Marcar como con error'}
                                            data-testid={`error-flag-${t.sale_id}`}
                                          >
                                            🚩
                                          </button>
                                        </td>
                                      )}
                                      {isAdmin && (
                                        <td className="px-2 py-1">
                                          <Input
                                            type="text"
                                            defaultValue={t.verification_note || ''}
                                            onBlur={(e) => {
                                              const v = e.target.value.trim();
                                              if (v !== (t.verification_note || '')) {
                                                updateTransferVerification(c.id, t.sale_id, { verification_note: v });
                                              }
                                            }}
                                            placeholder="Nota…"
                                            className="h-7 text-xs"
                                            data-testid={`note-input-${t.sale_id}`}
                                          />
                                        </td>
                                      )}
                                    </tr>
                                  );})}
                                </tbody>
                                <tfoot className="bg-slate-50 font-bold sticky bottom-0">
                                  <tr>
                                    <td colSpan="3" className="px-2 py-1 text-right">Total transferencias:</td>
                                    <td className="px-2 py-1 text-right text-sky-700">{c.totals_hidden ? '$ •••••' : fmtMoney(c.transfer_total)}</td>
                                    <td colSpan={isAdmin ? 4 : 1}></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.folio || 'Detalle de corte'}</DialogTitle>
            <DialogDescription>
              {detail && `${fmtDateTime(detail.started_at)} → ${fmtDateTime(detail.closed_at)}`}
            </DialogDescription>
          </DialogHeader>

          {!detail ? (
            <p>Cargando…</p>
          ) : (
            <>
              {(detail.notes || detail.other_overflow_note || detail.diff_reason) && (
                <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2 space-y-0.5" data-testid="cut-detail-notes">
                  {detail.notes && <div>📝 <b>Notas del corte:</b> <span className="whitespace-pre-wrap">{detail.notes}</span></div>}
                  {detail.other_overflow_note && <div>💵 <b>Sobrante otros (manual):</b> {detail.other_overflow_note} · {fmtMoney(detail.other_overflow || 0)}</div>}
                  {detail.diff_reason && <div>⚖️ <b>Motivo diferencia:</b> {detail.diff_reason}</div>}
                </div>
              )}
              <CutDetail detail={detail} onSaleClick={openSaleDetail} />
            </>
          )}

          <DialogFooter>
            {detail && (
              <Button
                variant="outline"
                onClick={() => openAddExpense(detail)}
                data-testid="add-retro-expense-btn"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                title={isAdmin ? 'Agrega un gasto a este corte (queda registrado dentro de su ventana)' : 'Solicita aprobación al admin'}
              >
                💸 Agregar gasto{!isAdmin && ' (aprobación)'}
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDetailOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saleDetailOpen} onOpenChange={setSaleDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sale-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🧾 Detalle de venta
              {saleDetail?.status === 'cancelled' && <Badge variant="destructive">Cancelada</Badge>}
            </DialogTitle>
            <DialogDescription>
              {saleDetail && `${fmtDateTime(saleDetail.created_at)} · ${saleDetail.payment_method}`}
            </DialogDescription>
          </DialogHeader>
          {!saleDetail ? <p>Cargando…</p> : <SaleDetailContent sale={saleDetail} />}
          <DialogFooter>
            {saleDetail && saleDetail.status !== 'cancelled' && (
              <>
                <Button
                  variant="outline"
                  onClick={openEditPayment}
                  data-testid="edit-sale-payment-btn"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  title={isAdmin ? 'Cambia directamente el método' : 'Solicita aprobación al admin'}
                >
                  💳 Cambiar método de pago{!isAdmin && ' (aprobación)'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditSellerDlg({ open: true, sellerId: saleDetail.seller_id || '', saving: false })}
                  data-testid="edit-sale-seller-btn"
                  className="border-sky-300 text-sky-700 hover:bg-sky-50"
                  title={isAdmin ? 'Atajo: Ctrl+Shift+V' : 'Solicita aprobación al admin'}
                >
                  👤 Cambiar vendedor{!isAdmin && ' (aprobación)'}
                </Button>
              </>
            )}
            {saleDetail && saleDetail.status !== 'cancelled' && (
              <Button
                variant="outline"
                onClick={() => openFullEdit(saleDetail)}
                data-testid="open-full-edit-sale"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                title={isAdmin ? 'Edita items, vendedor, cliente y método' : 'Solicita aprobación al admin'}
              >
                ✏️ Editar venta completa{!isAdmin && ' (aprobación)'}
              </Button>
            )}
            {saleDetail && saleDetail.status !== 'cancelled' && (
              <Button
                variant="destructive"
                onClick={() => openCancel(saleDetail)}
                data-testid="open-cancel-sale"
              >
                ❌ Cancelar venta
              </Button>
            )}
            <Button variant="ghost" onClick={() => setSaleDetailOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editPayDlg.open} onOpenChange={(o) => setEditPayDlg(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
          <DialogHeader>
            <DialogTitle>💳 Cambiar método de pago</DialogTitle>
            <DialogDescription>
              {saleDetail && (
                <>Venta total: <b>{fmtMoney(saleDetail.total)}</b>. El corte se actualizará automáticamente.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Método</Label>
              <select
                className="w-full border rounded p-2 mt-1"
                value={editPayDlg.method}
                onChange={(e) => setEditPayDlg(s => {
                  const total = Number(saleDetail?.total || 0);
                  return {
                    ...s,
                    method: e.target.value,
                    // Si pasa a un método único, pre-cargar todo en ese
                    split: e.target.value === 'mixto'
                      ? s.split
                      : {
                          efectivo: e.target.value === 'efectivo' ? total : 0,
                          tarjeta: e.target.value === 'tarjeta' ? total : 0,
                          transferencia: e.target.value === 'transferencia' ? total : 0,
                        }
                  };
                })}
                data-testid="edit-pay-method"
              >
                <option value="efectivo">💵 Efectivo</option>
                <option value="tarjeta">💳 Tarjeta</option>
                <option value="transferencia">🏦 Transferencia</option>
                <option value="mixto">🔀 Mixto</option>
              </select>
            </div>
            {editPayDlg.method === 'mixto' && (
              <div className="space-y-2 bg-violet-50 border border-violet-200 rounded p-3">
                <p className="text-xs text-muted-foreground">El total debe sumar {fmtMoney(saleDetail?.total || 0)}</p>
                {['efectivo', 'tarjeta', 'transferencia'].map(k => (
                  <div key={k} className="flex items-center gap-2">
                    <Label className="text-xs w-28 capitalize">{k}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editPayDlg.split[k]}
                      onChange={(e) => setEditPayDlg(s => ({ ...s, split: { ...s.split, [k]: parseFloat(e.target.value) || 0 } }))}
                      data-testid={`edit-pay-split-${k}`}
                    />
                  </div>
                ))}
                <div className="text-xs text-right font-semibold">
                  Suma: {fmtMoney(Object.values(editPayDlg.split).reduce((a,b)=>a+b,0))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditPayDlg(s => ({ ...s, open: false }))}>Cancelar</Button>
            <Button onClick={submitEditPayment} disabled={editPayDlg.saving} data-testid="edit-pay-submit">
              {editPayDlg.saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editSellerDlg.open} onOpenChange={(o) => setEditSellerDlg(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="edit-seller-dialog">
          <DialogHeader>
            <DialogTitle>👤 Cambiar vendedor</DialogTitle>
            <DialogDescription>
              Las comisiones de esta venta se reasignarán al vendedor nuevo. Requiere contraseña de admin.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Vendedor</Label>
            <select
              className="w-full border rounded p-2 mt-1"
              value={editSellerDlg.sellerId}
              onChange={(e) => setEditSellerDlg(s => ({ ...s, sellerId: e.target.value }))}
              data-testid="edit-seller-select"
            >
              <option value="">— Seleccionar —</option>
              {(sellers || []).filter(s => !s.is_local).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditSellerDlg(s => ({ ...s, open: false }))}>Cancelar</Button>
            <Button onClick={submitEditSeller} disabled={!editSellerDlg.sellerId} data-testid="edit-seller-submit">
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adminPwdDlg.open} onOpenChange={(o) => setAdminPwdDlg(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="admin-pwd-dialog">
          <DialogHeader>
            <DialogTitle>🔒 Confirmar como admin</DialogTitle>
            <DialogDescription>Escribe tu contraseña para aplicar este cambio.</DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            placeholder="Contraseña admin"
            value={adminPwdDlg.password}
            onChange={(e) => setAdminPwdDlg(s => ({ ...s, password: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter' && adminPwdDlg.password) confirmAdminPwd(); }}
            data-testid="admin-pwd-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdminPwdDlg({ open: false, password: '', purpose: null, saving: false })}>Cancelar</Button>
            <Button onClick={confirmAdminPwd} disabled={!adminPwdDlg.password || adminPwdDlg.saving} data-testid="admin-pwd-submit">
              {adminPwdDlg.saving ? 'Verificando…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <Warning size={22} weight="bold" /> {cancelTarget?._kind === 'shipment' ? 'Cancelar envío cobrado' : 'Cancelar venta'}
            </DialogTitle>
            <DialogDescription>
              {cancelTarget && (
                <>{cancelTarget?._kind === 'shipment' ? 'Folio' : 'Folio'} <code>{cancelTarget.folio || cancelTarget.id}</code> por <b>{fmtMoney(cancelTarget.total ?? cancelTarget.items_total ?? 0)}</b>.<br />
                  Si no eres administrador, se enviará una solicitud al admin para aprobación por Telegram.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Motivo *</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ej. Cliente devolvió mercancía, error en el cobro…"
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelling}>Volver</Button>
            <Button
              variant="destructive"
              onClick={submitCancel}
              disabled={cancelling || !cancelReason.trim()}
              data-testid="submit-cancel"
            >
              {cancelling ? 'Procesando…' : (cancelTarget?._kind === 'shipment' ? 'Cancelar envío' : 'Cancelar venta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fullEditDlg.open} onOpenChange={(o) => !o && !fullEditDlg.saving && setFullEditDlg({ open: false, sale: null, items: [], reason: '', saving: false })}>
        <DialogContent className="max-w-3xl" data-testid="full-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              ✏️ Editar venta completa
            </DialogTitle>
            <DialogDescription>
              {fullEditDlg.sale && (
                <>Venta <code>{fullEditDlg.sale.id?.slice(-8)}</code> · Total actual: <b>{fmtMoney(fullEditDlg.sale.total)}</b>.
                {!isAdmin && <span className="block text-amber-700 mt-1">⚠️ Tu solicitud requerirá aprobación del admin por Telegram.</span>}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              ⚠️ Al aplicar, el sistema reversa el inventario y comisiones actuales, y vuelve a generarlos con los nuevos valores.
              El corte se actualiza automáticamente.
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Items</Label>
              <div className="max-h-72 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-2 py-1">Producto</th>
                      <th className="text-right px-2 py-1 w-20">Cant.</th>
                      <th className="text-right px-2 py-1 w-24">Precio</th>
                      <th className="text-right px-2 py-1 w-24">Com. u.</th>
                      <th className="text-right px-2 py-1 w-20">Subtotal</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullEditDlg.items.map((it, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">
                          {it.product_name}{it.variant_name ? ` (${it.variant_name})` : ''}
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" min="1" value={it.quantity}
                            onChange={(e) => updateFullEditItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-7 text-right" data-testid={`fe-qty-${idx}`} />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" step="0.01" value={it.price}
                            onChange={(e) => updateFullEditItem(idx, 'price', parseFloat(e.target.value) || 0)}
                            className="h-7 text-right" data-testid={`fe-price-${idx}`} />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" step="0.01" value={it.commission}
                            onChange={(e) => updateFullEditItem(idx, 'commission', parseFloat(e.target.value) || 0)}
                            className="h-7 text-right" data-testid={`fe-com-${idx}`} />
                        </td>
                        <td className="px-2 py-1 text-right font-mono">
                          ${(Number(it.price) * Number(it.quantity)).toFixed(2)}
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button onClick={() => removeFullEditItem(idx)} className="text-rose-600 hover:bg-rose-50 px-1 rounded" title="Quitar">✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-bold">
                    <tr>
                      <td colSpan="4" className="px-2 py-1 text-right">Nuevo total:</td>
                      <td className="px-2 py-1 text-right text-emerald-700">
                        ${fullEditDlg.items.reduce((s, it) => s + (Number(it.price) * Number(it.quantity)), 0).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Para agregar productos nuevos: por ahora elimina items y crea una venta paralela. Si necesitas agregar, dime.
              </p>
            </div>
            {fullEditDlg.shipment_id && (
              <div>
                <Label className="text-xs flex items-center gap-1">🚚 Costo de envío</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={fullEditDlg.shipping_cost}
                  onChange={(e) => setFullEditDlg(s => ({ ...s, shipping_cost: e.target.value }))}
                  placeholder="0.00"
                  className="h-9"
                  data-testid="fe-shipping-cost"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Modifica el costo del envío ligado a esta venta (folio {fullEditDlg.sale?.shipments?.[0]?.folio || '—'}).
                  El total cobrado en el corte se recalcula automáticamente.
                </p>
              </div>
            )}
            <div>
              <Label className="text-xs">Motivo del cambio (recomendado)</Label>
              <Input value={fullEditDlg.reason} onChange={(e) => setFullEditDlg(s => ({ ...s, reason: e.target.value }))}
                placeholder="Ej: cliente devolvió 1 unidad" data-testid="fe-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setFullEditDlg({ open: false, sale: null, items: [], reason: '', saving: false })} disabled={fullEditDlg.saving}>Cancelar</Button>
              <Button onClick={submitFullEdit} disabled={fullEditDlg.saving || fullEditDlg.items.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fe-submit">
                {fullEditDlg.saving ? 'Procesando…' : (isAdmin ? 'Aplicar ahora' : 'Enviar a aprobación')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gasto retroactivo en corte cerrado */}
      <Dialog
        open={addExpDlg.open}
        onOpenChange={(o) => !o && !addExpDlg.saving && setAddExpDlg({ open: false, cut: null, amount: '', category: 'otros', description: '', reason: '', saving: false })}
      >
        <DialogContent className="max-w-md" data-testid="add-retro-expense-dialog">
          <DialogHeader>
            <DialogTitle>💸 Agregar gasto al corte</DialogTitle>
            <DialogDescription>
              {addExpDlg.cut && (
                <>Corte <code>{addExpDlg.cut.folio}</code>. El gasto se registrará dentro de la ventana de este corte
                  {!isAdmin && ' y necesita aprobación del admin'}.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Categoría</Label>
              <select
                value={addExpDlg.category}
                onChange={(e) => setAddExpDlg(s => ({ ...s, category: e.target.value }))}
                className="w-full border rounded h-9 px-2 text-sm"
                data-testid="add-exp-category"
              >
                <option value="salario">Salario</option>
                <option value="pago_envios">Pago a envíos</option>
                <option value="gasolina">Gasolina</option>
                <option value="otros">Otros</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Monto *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={addExpDlg.amount}
                onChange={(e) => setAddExpDlg(s => ({ ...s, amount: e.target.value }))}
                placeholder="0.00"
                data-testid="add-exp-amount"
              />
            </div>
            <div>
              <Label className="text-xs">
                Descripción {addExpDlg.category === 'otros' && <span className="text-rose-600">*</span>}
              </Label>
              <Input
                value={addExpDlg.description}
                onChange={(e) => setAddExpDlg(s => ({ ...s, description: e.target.value }))}
                placeholder="Detalle del gasto"
                data-testid="add-exp-description"
              />
            </div>
            {!isAdmin && (
              <div>
                <Label className="text-xs">Motivo para aprobación *</Label>
                <Textarea
                  value={addExpDlg.reason}
                  onChange={(e) => setAddExpDlg(s => ({ ...s, reason: e.target.value }))}
                  placeholder="Ej: olvidé registrar la gasolina del lunes"
                  rows={2}
                  data-testid="add-exp-reason"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAddExpDlg({ open: false, cut: null, amount: '', category: 'otros', description: '', reason: '', saving: false })}
              disabled={addExpDlg.saving}
            >Cancelar</Button>
            <Button
              onClick={submitAddExpense}
              disabled={addExpDlg.saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="add-exp-submit"
            >
              {addExpDlg.saving ? 'Procesando…' : (isAdmin ? 'Agregar gasto' : 'Enviar a aprobación')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aprobación de cierre con envíos atrasados */}
      <Dialog
        open={overdueDlg.open}
        onOpenChange={(o) => !o && !overdueDlg.sending && setOverdueDlg({ open: false, shipments: [], sending: false })}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="overdue-approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              ⏰ Cierre requiere aprobación del admin
            </DialogTitle>
            <DialogDescription>
              Hay <b>{overdueDlg.shipments.length}</b> envío(s) asignado(s) hace más de <b>7 días</b> sin cobrar.
              Para cerrar el corte con estos atrasos, se debe enviar una solicitud al admin por Telegram.
              Al aprobar, el corte se cierra automáticamente con los datos que ingresaste.
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded max-h-72 overflow-y-auto text-xs" data-testid="overdue-list">
            <table className="w-full">
              <thead className="bg-amber-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Folio</th>
                  <th className="px-2 py-1 text-left">Entregador</th>
                  <th className="px-2 py-1 text-left">Cliente</th>
                  <th className="px-2 py-1 text-right">Monto</th>
                  <th className="px-2 py-1 text-right">Días</th>
                </tr>
              </thead>
              <tbody>
                {overdueDlg.shipments.map((sh) => (
                  <tr key={sh.id || sh.folio} className="border-t hover:bg-amber-50/40" data-testid={`overdue-row-${sh.id || sh.folio}`}>
                    <td className="px-2 py-1 font-semibold">{sh.folio || sh.id}</td>
                    <td className="px-2 py-1">{sh.deliverer_name || '—'}</td>
                    <td className="px-2 py-1">{sh.client_name || '—'}</td>
                    <td className="px-2 py-1 text-right">{fmtMoney((sh.items_total || 0) + (sh.shipping_cost || 0))}</td>
                    <td className="px-2 py-1 text-right">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${(sh.days_late || 0) > 14 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                        {sh.days_late ?? '?'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOverdueDlg({ open: false, shipments: [], sending: false })}
              disabled={overdueDlg.sending}
            >Cancelar</Button>
            <Button
              onClick={requestOverdueApproval}
              disabled={overdueDlg.sending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="request-overdue-approval-btn"
            >
              {overdueDlg.sending ? 'Enviando…' : '📩 Solicitar aprobación al admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </MainLayout>
  );
};

const SummaryCard = ({ icon, label, value, color = 'slate', subtle }) => (
  <div className={`border rounded-lg p-3 bg-${color}-50 border-${color}-200`}>
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className={`text-${color}-600`}>{icon}</span>
      <span>{label}</span>
    </div>
    <div className={`text-xl font-bold text-${color}-700`}>{value}</div>
    {subtle && <div className="text-xs text-muted-foreground mt-0.5">{subtle}</div>}
  </div>
);

const Row = ({ label, value, color, bold, testId, subtle }) => (
  <div className="flex items-start justify-between text-sm" data-testid={testId}>
    <span className="text-muted-foreground">
      {label}
      {subtle && <span className="block text-[10px] text-slate-400 mt-0.5">{subtle}</span>}
    </span>
    <span className={`${bold ? 'font-bold' : ''} ${color ? `text-${color}-600` : ''}`}>{value}</span>
  </div>
);

const CutDetail = ({ detail, onSaleClick }) => {
  const t = detail.totals || {};
  const sellersAgg = useMemo(() => {
    const m = {};
    (detail.sales || []).forEach(s => {
      const k = s.seller_id || 'sin';
      if (!m[k]) m[k] = { name: s.seller_name || 'Sin vendedor', count: 0, total: 0, commission: 0 };
      m[k].count += 1;
      m[k].total += s.total || 0;
    });
    (detail.commissions || []).forEach(c => {
      if (m[c.seller_id]) m[c.seller_id].commission += c.amount || 0;
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [detail]);

  const productsAgg = useMemo(() => {
    const m = {};
    (detail.sales || []).forEach(s => {
      (s.items || []).forEach(it => {
        const k = it.product_id;
        if (!m[k]) m[k] = { name: it.product_name || k, qty: 0, total: 0 };
        m[k].qty += it.quantity || 0;
        m[k].total += (it.unit_price || 0) * (it.quantity || 0);
      });
    });
    return Object.values(m).sort((a, b) => b.qty - a.qty).slice(0, 25);
  }, [detail]);

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Ventas" value={t.sales_count || 0} />
        <Stat label="Total" value={fmtMoney(t.sales_total)} />
        <Stat label="Efectivo" value={fmtMoney(t.cash_total)} />
        <Stat label="Transferencia" value={fmtMoney(t.transfer_total)} />
        <Stat label="Tarjeta" value={fmtMoney(t.card_total)} />
        <Stat label="Sobrante envío" value={fmtMoney(t.shipping_overflow)} />
        <Stat label="Sobrante otros" value={fmtMoney(detail.other_overflow)} />
        <Stat label="Salidas" value={`${t.exits_qty || 0} pzas`} />
      </div>

      {(detail.expenses || []).length > 0 && (
        <div>
          <div className="font-semibold mb-2">🧾 Gastos del corte</div>
          <ExpensesList expenses={detail.expenses} />
        </div>
      )}

      {detail.counted_total !== undefined && (
        <div className="bg-slate-50 border rounded p-3">
          <div className="font-semibold mb-2">💵 Conciliación de efectivo</div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <Stat label="Esperado" value={fmtMoney(detail.expected_cash)} />
            <Stat label="Contado" value={fmtMoney(detail.counted_total)} />
            <Stat label="Diferencia"
              value={fmtMoney(detail.cash_diff)}
              color={detail.cash_diff === 0 ? 'emerald' : (detail.cash_diff > 0 ? 'sky' : 'rose')} />
          </div>
          {detail.counted_cash && (
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-1 text-xs">
              {DENOMS.map(d => (detail.counted_cash[d] || 0) > 0 && (
                <div key={d} className="bg-white border rounded px-2 py-1">
                  <span className="text-muted-foreground">${d}</span> × <b>{detail.counted_cash[d]}</b>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="font-semibold mb-2">👤 Vendedores</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-2">Vendedor</th>
              <th className="text-right p-2">Ventas</th>
              <th className="text-right p-2">Total</th>
              <th className="text-right p-2">Comisión</th>
            </tr>
          </thead>
          <tbody>
            {sellersAgg.map((s, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">{s.name}</td>
                <td className="p-2 text-right">{s.count}</td>
                <td className="p-2 text-right">{fmtMoney(s.total)}</td>
                <td className="p-2 text-right">{fmtMoney(s.commission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="font-semibold mb-2">📦 Productos vendidos (top 25)</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-2">Producto</th>
              <th className="text-right p-2">Cantidad</th>
              <th className="text-right p-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {productsAgg.map((p, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">{p.name}</td>
                <td className="p-2 text-right">{p.qty}</td>
                <td className="p-2 text-right">{fmtMoney(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="font-semibold mb-2">🧾 Ventas ({(detail.sales || []).length})</div>
        <p className="text-xs text-muted-foreground mb-1">Click en una venta para ver el detalle.</p>
        <div className="max-h-72 overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-2">Hora</th>
                <th className="text-left p-2">Vendedor</th>
                <th className="text-left p-2">Pago</th>
                <th className="text-right p-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {(detail.sales || []).map((s, i) => {
                const pmStyles = {
                  efectivo: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                  tarjeta: 'bg-sky-100 text-sky-800 border-sky-300',
                  transferencia: 'bg-amber-100 text-amber-800 border-amber-300',
                  mixto: 'bg-violet-100 text-violet-800 border-violet-300',
                };
                const pmIcons = { efectivo: '💵', tarjeta: '💳', transferencia: '🏦', mixto: '🔀' };
                const pm = (s.payment_method || '').toLowerCase();
                return (
                <tr
                  key={i}
                  className={`border-t cursor-pointer hover:bg-violet-50 ${s.status === 'cancelled' ? 'opacity-50 line-through' : ''}`}
                  onClick={() => onSaleClick && onSaleClick(s)}
                  data-testid={`history-sale-${s.id}`}
                >
                  <td className="p-2">{fmtDateTime(s.created_at)}</td>
                  <td className="p-2">{s.seller_name}</td>
                  <td className="p-2">
                    <Badge variant="outline" className={`text-xs ${pmStyles[pm] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                      {pmIcons[pm] || '•'} {s.payment_method || '—'}
                    </Badge>
                    {pm === 'mixto' && s.payment_split && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {Object.entries(s.payment_split)
                          .filter(([, v]) => Number(v) > 0)
                          .map(([k, v]) => `${k.slice(0, 4)} ${fmtMoney(v)}`)
                          .join(' + ')}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right">{fmtMoney(s.total)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(detail.exits || []).length > 0 && (
        <div>
          <div className="font-semibold mb-2">📤 Salidas ({detail.exits.length})</div>
          <div className="space-y-1">
            {detail.exits.map(e => (
              <div key={e.id} className="text-xs bg-rose-50 border border-rose-200 rounded p-2">
                <b>{e.folio}</b> · {fmtDateTime(e.created_at)} · {e.total_qty} pzas — {e.reason}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="border rounded p-2 bg-white">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-base font-bold ${color ? `text-${color}-600` : ''}`}>{value}</div>
  </div>
);

const SaleDetailContent = ({ sale }) => (
  <div className="space-y-3 text-sm">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      <Stat label="Total" value={fmtMoney(sale.total)} color="violet" />
      <Stat label="Pago" value={sale.payment_method || '—'} />
      <Stat label="Vendedor" value={sale.seller_name || '—'} />
      {sale.client_name && <Stat label="Cliente" value={sale.client_name} />}
      {sale.client_phone && <Stat label="Teléfono" value={sale.client_phone} />}
      {sale.has_shipment && <Stat label="Envío" value={`${sale.shipment_folio || 'sí'} · ${fmtMoney(sale.shipment_cost || 0)}`} color="amber" />}
    </div>

    {(() => {
      const pm = (sale.payment_method || '').toLowerCase();
      const split = sale.payment_split || {};
      const isMixto = pm === 'mixto';
      if (isMixto) {
        const parts = Object.entries(split).filter(([, v]) => Number(v) > 0);
        if (parts.length === 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
              <div className="font-semibold text-amber-800">🔀 Pago mixto</div>
              <div className="text-xs text-muted-foreground mt-1">
                Esta venta se registró antes del fix del modelo; no tiene el desglose guardado.
                En el balance se cuenta como efectivo por defecto.
              </div>
            </div>
          );
        }
        return (
          <div className="bg-violet-50 border border-violet-200 rounded p-3 text-sm">
            <div className="font-semibold text-violet-800 mb-1">🔀 Pago mixto</div>
            <div className="grid grid-cols-3 gap-2">
              {parts.map(([k, v]) => (
                <div key={k} className="bg-white rounded p-2 text-center">
                  <div className="text-xs text-muted-foreground capitalize">{k}</div>
                  <div className="font-bold">{fmtMoney(v)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      }
      const labels = {
        efectivo: { ico: '💵', label: 'Efectivo', cls: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
        tarjeta: { ico: '💳', label: 'Tarjeta', cls: 'bg-sky-50 border-sky-200 text-sky-800' },
        transferencia: { ico: '🏦', label: 'Transferencia', cls: 'bg-amber-50 border-amber-200 text-amber-800' },
      };
      const cfg = labels[pm];
      if (!cfg) return null;
      return (
        <div className={`border rounded p-3 text-sm flex items-center justify-between ${cfg.cls}`}>
          <div className="font-semibold">{cfg.ico} Pago en {cfg.label.toLowerCase()}</div>
          <div className="font-bold text-base">{fmtMoney(sale.total)}</div>
        </div>
      );
    })()}

    <div>
      <div className="font-semibold mb-1">📦 Productos</div>
      <table className="w-full text-xs border rounded overflow-hidden">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left p-2">Producto</th>
            <th className="text-right p-2">Cantidad</th>
            <th className="text-left p-2">Nivel</th>
            <th className="text-right p-2">P. Unitario</th>
            <th className="text-right p-2">Subtotal</th>
            <th className="text-right p-2">Comisión</th>
          </tr>
        </thead>
        <tbody>
          {(sale.items || []).map((it, i) => (
            <tr key={i} className="border-t">
              <td className="p-2">{it.product_name}</td>
              <td className="p-2 text-right">{it.quantity}</td>
              <td className="p-2">{it.price_type}</td>
              <td className="p-2 text-right">{fmtMoney(it.unit_price)}</td>
              <td className="p-2 text-right font-semibold">{fmtMoney((it.unit_price || 0) * (it.quantity || 0))}</td>
              <td className="p-2 text-right text-emerald-700">{fmtMoney(it.commission || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {(sale.commissions || []).length > 0 && (
      <div>
        <div className="font-semibold mb-1">💰 Comisiones</div>
        <div className="text-xs space-y-1">
          {sale.commissions.map(c => (
            <div key={c.id} className="bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center justify-between">
              <span>Comisión por producto</span>
              <span className="font-semibold">{fmtMoney(c.amount)} · {c.paid ? '✅ Pagada' : '✅ Cobrada (en corte)'}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {(sale.shipments || []).length > 0 && (
      <div>
        <div className="font-semibold mb-1">🚚 Envíos</div>
        <div className="text-xs space-y-1">
          {sale.shipments.map(sh => (
            <div key={sh.id} className="bg-amber-50 border border-amber-200 rounded p-2">
              <div className="flex justify-between">
                <b>{sh.folio}</b>
                <Badge variant="outline">{sh.status}</Badge>
              </div>
              <div className="text-muted-foreground">
                {sh.shipping_address} · {sh.client_name || '—'} · costo {fmtMoney(sh.shipping_cost)}
                {sh.deliverer_keeps_shipping && <span className="ml-1">(auto propio)</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {sale.status === 'cancelled' && (
      <div className="bg-rose-50 border border-rose-300 rounded p-3 text-sm">
        <div className="font-bold text-rose-700">❌ Venta cancelada</div>
        {sale.cancel_reason && <div className="text-xs mt-1"><b>Motivo:</b> {sale.cancel_reason}</div>}
        {sale.cancelled_at && <div className="text-xs">{fmtDateTime(sale.cancelled_at)}</div>}
      </div>
    )}
  </div>
);

export default Cuts;
